import { AdminCalendar, CalendarSearchBarIcon } from 'components/icons'
import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import Label from 'components/form/Label'
import { dateWithoutTimezone, formatDefault, getDisabledSingularDates } from 'data/helpers/dates'


/**
 * @param { Object | null | undefined } overheadLabel
 * @returns
 */
const DayPickerDoubleDesign = ({
  align = 'left',
  disabledDays = [],
  errors,
  formatDate,
  label,
  overheadLabel = null,
  overheadLabelClassNames,
  classNames = '',
  subClassNames,
  Icon = null,
  onDayChange,
  selected,
  variant,
  disabled = false,
}) => {
  const isPublic = variant == 'public_booking' || variant == 'public_search'
  const dataEntry = isPublic || variant == 'admin_double_field_data_entry'

  const [selectedStartDate, setSelectedStartDate] = useState(selected ? selected.from : null)
  const [selectedEndDate, setSelectedEndDate] = useState(selected ? selected.to : null)
  const [invalidDates, setInvalidDates] = useState([])
  const dayPickerArrival = useRef(null)
  const dayPickerDeparture = useRef(null)

  const [modifiers, setModifiers] = useState({
    selected_start: selectedStartDate,
    selected_end: selectedEndDate,
    selected_range: {
      from: selectedStartDate,
      to: selectedEndDate,
    },
  })

  const onDayClick = (date, isFirstField) => {
    if (!date) {
      if (isFirstField) {
        setSelectedStartDate(null)
        setSelectedEndDate(null)
      } else {
        setSelectedEndDate(null)
      }
    }

    if (date) {
      let soonestAvailableDate = null
      let previousUnavailableDate = null

      disabledDays.map((dates) => {
        if (date < dates.after && dates.after < soonestAvailableDate) {
          soonestAvailableDate = dates.after
        } else if (!soonestAvailableDate && dates.after > date) {
          soonestAvailableDate = dates.after
        }
        if (date > dates.before && dates.before > previousUnavailableDate) {
          previousUnavailableDate = dates.before
        } else if (!previousUnavailableDate && dates.before < date) {
          previousUnavailableDate = dates.before
        }
      })
      setInvalidDates([{ after: soonestAvailableDate, before: previousUnavailableDate }])
    } else if (!date, isFirstField) {
      setInvalidDates([])
    }

    if (date) {
      if (isFirstField) {
        if (selectedStartDate !== null && selectedEndDate !== null && date > selectedEndDate) {
          setSelectedEndDate(null)
        }
        setSelectedStartDate(date)
        dayPickerDeparture.current.getInput().focus()
        dayPickerDeparture.current.showDayPicker()
      } else {
        setSelectedEndDate(date)
      }
    }
  }

  const DynamicIcon = () => {
    if (Icon) {
      return (<div className="absolute top-0 bottom-0 flex items-center justify-center my-auto pointer-events-none right-20 text-22">
        {Icon()}
      </div>)
    } else if (isPublic) {
      return (
        <CalendarSearchBarIcon
          className="absolute top-0 bottom-0 justify-center hidden my-auto pointer-events-none text-primary right-20 lg:flex text-22" />
      )
    } else {
      return (
        <AdminCalendar
          className="absolute top-0 bottom-0 justify-center hidden my-auto pointer-events-none text-primary right-20 lg:flex text-22" />
      )
    }
  }

  useEffect(() => {
    onDayChange({
      from: formatDefault(selectedStartDate),
      to: formatDefault(selectedEndDate),
    })

    setModifiers({
      selected_start: selectedStartDate,
      selected_end: selectedEndDate,
      selected_range: selectedStartDate && selectedEndDate ? { from: selectedStartDate, to: selectedEndDate } : null,
    })
  }, [selectedEndDate, selectedStartDate])

  useEffect(() => {
    if (selected) {
      if (String(selectedStartDate) !== String(selected.from)) {
        setSelectedStartDate(selected.from)
      }
      if (String(selectedEndDate) !== String(selected.to)) {
        setSelectedEndDate(selected.to)
      }
    }
  }, [selected])

  return (
    <div className={`${classNames ? classNames : 'flex flex-col gap-20'}`}>
      <div className="w-full py-5">
        {
          overheadLabel
          && <Label className={overheadLabelClassNames}>
            {overheadLabel.from}
          </Label>
        }
        <div className={`relative ${isPublic ? 'DayPickerPublic' : 'DayPickerAdmin'} align-${align} ${subClassNames} ${disabled && 'bg-grey-100'}`}>
          <DayPickerInput
            placeholder={label ? label[0] : 'Arrival'}
            formatDate={formatDate}
            ref={dayPickerArrival}
            onDayChange={(date) => onDayClick(dateWithoutTimezone(date), true)}
            value={selectedStartDate}
            dayPickerProps={{
              modifiers,
              month: selectedStartDate,
              selectedDays: { from: selectedStartDate, to: selectedEndDate },
              numberOfMonths: isPublic ? 2 : 1,
              weekdaysShort: isPublic
                ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
              disabledDays: [...disabledDays, ...invalidDates]
                .concat(dataEntry
                  ? [
                    { before: new Date() },
                    selectedEndDate ? { after: selectedEndDate } : {},
                    ...getDisabledSingularDates(disabledDays, true),
                  ]
                  : []),
            }}
            inputProps={{
              readOnly: true,
              disabled: disabled,
            }}
          />
          <DynamicIcon />
          <div className="absolute bottom-0 antialiased left-20 text-12 text-red">{errors?.arrival?.message}</div>
        </div>
      </div>

      <div className="w-full">
        {
          overheadLabel
          && <Label className={overheadLabelClassNames}>
            {overheadLabel.to}
          </Label>
        }
        <div className={`relative ${isPublic ? 'DayPickerPublic' : 'DayPickerAdmin'} align-${align} ${subClassNames} ${disabled && 'bg-grey-100'}`}>
          <DayPickerInput
            formatDate={formatDate}
            ref={dayPickerDeparture}
            placeholder={label ? label[1] : 'Departure'}
            onDayChange={(date) => onDayClick(dateWithoutTimezone(date), false)}
            value={selectedEndDate}
            dayPickerProps={{
              modifiers,
              month: selectedStartDate,
              selectedDays: { from: selectedStartDate, to: selectedEndDate },
              numberOfMonths: isPublic ? 2 : 1,
              weekdaysShort: isPublic
                ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
              disabledDays: [...disabledDays, ...invalidDates]
                .concat(dataEntry
                  ? [
                    { before: selectedStartDate ?? new Date() },
                    ...getDisabledSingularDates(disabledDays, false),
                  ]
                  : []),
            }}
            inputProps={{
              readOnly: true,
              disabled: disabled,
            }}
          />
          <DynamicIcon />
          <div className="absolute bottom-0 antialiased left-20 text-12 text-red">{errors?.departure?.message}</div>
        </div>
      </div>
    </div>
  )
}

DayPickerDoubleDesign.propTypes = {
  align: PropTypes.string,
  classNames: PropTypes.string,
  dates: PropTypes.object,
  disabledDays: PropTypes.array,
  errors: PropTypes.object,
  formatDate: PropTypes.func,
  Icon: PropTypes.func,
  label: PropTypes.array,
  overheadLabel: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }),
  overheadLabelClassNames: PropTypes.string,
  onDayChange: PropTypes.func,
  parseDate: PropTypes.func,
  selected: PropTypes.object,
  subClassNames: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
}

export default DayPickerDoubleDesign
